import type { ContentMasterWorkspaceDefaultLanguageView } from '../api/generated/models/ContentMasterWorkspaceDefaultLanguageView';
import { ContentMasterWorkspacePlatformView } from '../api/generated/models/ContentMasterWorkspacePlatformView';

/**
 * `source` value the API expects everywhere - composite identifier from
 * `GET /content-master/workspace-config.platforms[].id`. Two shapes:
 *   - `"plattform:N"` - master / marketplace level (`kPlattform=N, kShop=0`)
 *   - `"shop:N"`      - per-shop override (`kShop=N`)
 * Server-side validator: `^(plattform|shop):\d+$`.
 */
export type ContentMasterChannelSource = string;

export const CONTENT_MASTER_SOURCE_REGEX = /^(plattform|shop):\d+$/;

/** Cheap runtime guard mirroring the backend validator. */
export function isContentMasterSourceLiteral(raw: unknown): raw is string {
  return typeof raw === 'string' && CONTENT_MASTER_SOURCE_REGEX.test(raw);
}

/** API `language` string from workspace default (iso2 or kSprache). */
export function workspaceDefaultLanguageToApiValue(
  d: ContentMasterWorkspaceDefaultLanguageView | null | undefined
): string {
  if (!d) {
    return '';
  }
  const iso = d.iso2?.trim().toLowerCase();
  if (iso && iso.length >= 2) {
    return iso.slice(0, 2);
  }
  if (d.kSprache != null) {
    return String(d.kSprache);
  }
  return '';
}

/**
 * `tPlattform.nTyp = 1` is the master JTL-Wawi platform - the canonical
 * source the backend prompt-engineering treats as "the product's primary
 * content". Used to tag the option with a Master badge / tooltip.
 */
export function isMasterJtlWawiChannel(p: ContentMasterWorkspacePlatformView): boolean {
  return p.kind === ContentMasterWorkspacePlatformView.kind.PLATFORM && p.nTyp === 1;
}

/**
 * Onlineshop default platform (`tPlattform.nTyp = 2`, `kPlattform=2`) -
 * the second-best fallback when no master is present.
 */
const isOnlineshopDefaultPlatform = (p: ContentMasterWorkspacePlatformView): boolean =>
  p.kind === ContentMasterWorkspacePlatformView.kind.PLATFORM && p.kPlattform === 2;

/**
 * Returns true when `source` is a valid `id` of any channel in the current
 * workspace snapshot. Use to validate URL/state after `workspace-config` loads.
 */
export function isContentMasterChannelKnown(
  source: string,
  platforms: ContentMasterWorkspacePlatformView[] | undefined
): boolean {
  if (!source || !platforms?.length) {
    return false;
  }
  return platforms.some((p) => p.id === source);
}

/**
 * Pick the default channel id when the user has no explicit selection.
 * Priority follows the v2 backend prompt:
 *   1) Master JTL-Wawi (`kind=platform && nTyp=1`) - best universal default.
 *   2) Onlineshop default (`kind=platform && kPlattform=2`).
 *   3) First active `kind=shop` with `kPlattform != null`.
 *   4) First channel with a usable composite id.
 */
export function pickDefaultContentMasterChannelId(
  platforms: ContentMasterWorkspacePlatformView[] | undefined
): string | null {
  if (!platforms?.length) {
    return null;
  }
  const master = platforms.find(
    (p) => isMasterJtlWawiChannel(p) && isContentMasterSourceLiteral(p.id)
  );
  if (master?.id) {
    return master.id;
  }
  const onlineshop = platforms.find(
    (p) => isOnlineshopDefaultPlatform(p) && isContentMasterSourceLiteral(p.id)
  );
  if (onlineshop?.id) {
    return onlineshop.id;
  }
  const activeShop = platforms.find(
    (p) =>
      p.kind === ContentMasterWorkspacePlatformView.kind.SHOP &&
      p.active === true &&
      p.kPlattform != null &&
      isContentMasterSourceLiteral(p.id)
  );
  if (activeShop?.id) {
    return activeShop.id;
  }
  const firstUsable = platforms.find((p) => isContentMasterSourceLiteral(p.id));
  return firstUsable?.id ?? null;
}

/** One option for the channel `<select>`. */
export interface ContentMasterChannelOption {
  /** Composite id (e.g. `"plattform:1"`, `"shop:5"`) - sent to API as `source`. */
  value: string;
  /** Discriminator from the API. */
  kind: ContentMasterWorkspacePlatformView.kind;
  /** Display name (`tPlattform.cName` or `tShop.cName`). */
  name: string;
  /** "Verkaufskanaltyp" / category label from `nTypLabel`. Empty string when missing. */
  nTypLabel: string;
  /** Resolved `tPlattform.cName` or null. */
  plattformName: string | null;
  /** `nAktiv === 1` for shops; always true for platforms. */
  active: boolean;
  /**
   * `kPlattform === null` ⇔ shop has no `tArtikelBeschreibung` yet AND no
   * fallback platform - backend will reject any API call. Selectable but
   * flagged so the UI can show a tooltip.
   */
  hasContent: boolean;
  /** True for the master JTL-Wawi platform (`nTyp === 1`). */
  isMaster: boolean;
}

export interface ContentMasterChannelGroup {
  key: string;
  /** Discriminator: one platforms group first, then one group per shop type. */
  kind: ContentMasterWorkspacePlatformView.kind;
  /**
   * Group header for shop groups - the channel-type label from Wawi
   * (`plattformName` / `nTypLabel`, e.g. "Marktplatz", "Onlineshop").
   * `null` for the platforms group and for shops without a type label -
   * callers render their generic i18n label instead.
   */
  label: string | null;
  options: ContentMasterChannelOption[];
}

const toOption = (
  p: ContentMasterWorkspacePlatformView
): ContentMasterChannelOption | null => {
  if (!isContentMasterSourceLiteral(p.id) || !p.kind) {
    return null;
  }
  const name = (p.name ?? '').trim() || p.id;
  return {
    value: p.id,
    kind: p.kind,
    name,
    nTypLabel: (p.nTypLabel ?? '').trim(),
    plattformName: p.plattformName ?? null,
    // Platforms have no `nAktiv` flag in Wawi → backend hard-codes `active=true`.
    active: p.kind === ContentMasterWorkspacePlatformView.kind.SHOP ? p.active === true : true,
    // For platform-level rows the backend always knows kPlattform; for shop-
    // level rows kPlattform is null only when no description exists yet AND
    // no fallback could be inferred (in that case the backend will 400).
    hasContent:
      p.kind === ContentMasterWorkspacePlatformView.kind.SHOP ? p.kPlattform != null : true,
    isMaster: isMasterJtlWawiChannel(p),
  };
};

/**
 * Resolve a raw `source` literal (e.g. `shop:1`, `plattform:5`) to its
 * workspace display name. Falls back to the raw value when the channel is not
 * part of the current workspace snapshot. Shared by the list and detail pages
 * so the "processed on other sources" tooltip labels stay consistent.
 */
export function resolveContentMasterSourceLabel(
  options: ContentMasterChannelOption[],
  source: string
): string {
  return options.find((o) => o.value === source)?.name ?? source;
}

/** Flat list of channel options in API order. */
export function buildContentMasterChannelOptions(
  platforms: ContentMasterWorkspacePlatformView[] | undefined
): ContentMasterChannelOption[] {
  if (!platforms?.length) {
    return [];
  }
  const out: ContentMasterChannelOption[] = [];
  for (const p of platforms) {
    const opt = toOption(p);
    if (opt) {
      out.push(opt);
    }
  }
  return out;
}

/**
 * Channel-type split for the `<select>`: the stable `platforms` group first
 * (master / marketplace defaults that JTL inherits from), then one group per
 * shop type (Marktplatz / SCX, Onlineshop, ...) keyed by the same label the
 * options previously carried after the dash - the header now names the type,
 * so the per-option suffix is gone. Shop groups keep the API first-appearance
 * order; empty groups are omitted.
 */
export function buildContentMasterChannelGroups(
  platforms: ContentMasterWorkspacePlatformView[] | undefined
): ContentMasterChannelGroup[] {
  const flat = buildContentMasterChannelOptions(platforms);
  if (!flat.length) {
    return [];
  }
  const groups: ContentMasterChannelGroup[] = [];
  const platformOptions = flat.filter(
    (o) => o.kind === ContentMasterWorkspacePlatformView.kind.PLATFORM
  );
  if (platformOptions.length) {
    groups.push({
      key: 'platforms',
      kind: ContentMasterWorkspacePlatformView.kind.PLATFORM,
      label: null,
      options: platformOptions,
    });
  }
  const shopGroups = new Map<string, ContentMasterChannelGroup>();
  for (const opt of flat) {
    if (opt.kind !== ContentMasterWorkspacePlatformView.kind.SHOP) {
      continue;
    }
    const meta = formatContentMasterChannelMeta(opt);
    const label = meta !== '-' ? meta : null;
    const key = `shops:${label ?? ''}`;
    let group = shopGroups.get(key);
    if (!group) {
      group = {
        key,
        kind: ContentMasterWorkspacePlatformView.kind.SHOP,
        label,
        options: [],
      };
      shopGroups.set(key, group);
      groups.push(group);
    }
    group.options.push(opt);
  }
  return groups;
}

/**
 * Format the right-hand side of an option:
 *   - platform → `nTypLabel` (e.g. `"Master (JTL-Wawi)"`, `"Onlineshop (default)"`)
 *   - shop     → `plattformName` (the platform the shop runs on, e.g. `"Onlineshop"`,
 *                `"JTL-Connector"`); falls back to `nTypLabel` then `"-"`.
 */
export function formatContentMasterChannelMeta(opt: ContentMasterChannelOption): string {
  if (opt.kind === ContentMasterWorkspacePlatformView.kind.PLATFORM) {
    return opt.nTypLabel || '-';
  }
  const name = opt.plattformName?.trim();
  if (name) {
    return name;
  }
  return opt.nTypLabel || '-';
}

/* ------------------------------------------------------------------ */
/*  Last-selected-channel persistence per Wawi (keyed by dataSourceId) */
/* ------------------------------------------------------------------ */

const CHANNEL_STORAGE_PREFIX = 'cm_last_channel:';

const safeLocalStorage = (): Storage | null => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return null;
    }
    return window.localStorage;
  } catch {
    return null;
  }
};

/** Read the last manually-selected channel id for this Wawi, or null. */
export function readLastSelectedChannelId(dataSourceId: string | null | undefined): string | null {
  if (!dataSourceId) {
    return null;
  }
  const ls = safeLocalStorage();
  if (!ls) {
    return null;
  }
  try {
    const raw = ls.getItem(CHANNEL_STORAGE_PREFIX + dataSourceId);
    return isContentMasterSourceLiteral(raw) ? raw : null;
  } catch {
    return null;
  }
}

/**
 * Persist the user's channel pick. No-op on quota / privacy errors -
 * persistence is a UX nicety, not a correctness requirement.
 */
export function writeLastSelectedChannelId(
  dataSourceId: string | null | undefined,
  source: string | null | undefined
): void {
  if (!dataSourceId || !source || !isContentMasterSourceLiteral(source)) {
    return;
  }
  const ls = safeLocalStorage();
  if (!ls) {
    return;
  }
  try {
    ls.setItem(CHANNEL_STORAGE_PREFIX + dataSourceId, source);
  } catch {
    // ignore
  }
}

/**
 * Compute the initial channel id to seed Redux/URL with on first mount,
 * preferring (1) URL value, (2) localStorage for this dataSourceId,
 * (3) the prioritised default from `pickDefaultContentMasterChannelId`.
 * Each candidate is validated against the current `platforms` snapshot.
 */
export function resolveInitialContentMasterChannelId(args: {
  fromUrl: string;
  dataSourceId: string | null | undefined;
  platforms: ContentMasterWorkspacePlatformView[] | undefined;
}): string {
  const { fromUrl, dataSourceId, platforms } = args;
  if (fromUrl && isContentMasterChannelKnown(fromUrl, platforms)) {
    return fromUrl;
  }
  const persisted = readLastSelectedChannelId(dataSourceId);
  if (persisted && isContentMasterChannelKnown(persisted, platforms)) {
    return persisted;
  }
  return pickDefaultContentMasterChannelId(platforms) ?? '';
}
